import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray, useWatch, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ExclamationTriangleIcon,
  LockClosedIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { documentsApi } from '@services/api';
import { getApiErrorStatus } from '@services/api';
import {
  documentFormSchema,
  blankDocumentForm,
  emptyLineItem,
  formatMoney,
  previewTotals,
} from '@utils';
import type { DocumentFormValues } from '@utils';
import { ClientCombobox } from '@components/documents/ClientCombobox';
import { LoadingSpinner, EmptyState } from '@components/common';
import type {
  CreateDocumentInput,
  DocumentDetail,
  UpdateDocumentInput,
} from '@types';

const inputClass =
  'block h-11 w-full rounded-[9px] border-[1.5px] border-[#d7dfe9] px-3.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';
const labelClass = 'mb-1.5 block text-[12.5px] font-semibold text-gray-700';
const card = 'rounded-2xl border border-hairline bg-white p-6 shadow-sm';
const sectionHead = 'text-[15px] font-semibold text-gray-900';

function detailToForm(doc: DocumentDetail): DocumentFormValues {
  return {
    type: doc.type,
    client_id: doc.client_id,
    contact: doc.contact ?? '',
    event: doc.event_name ?? '',
    prepared_label: doc.prepared_label ?? '',
    terms: doc.terms ?? '',
    accept_line: doc.accept_line ?? '',
    issue_date: doc.issue_date ?? '',
    due_date: doc.due_date ?? '',
    valid_until: doc.valid_until ?? '',
    discount: doc.discount === '0.00' ? '' : doc.discount,
    line_items: doc.line_items.length
      ? doc.line_items.map(li => ({
          description: li.description,
          amount: li.amount,
        }))
      : [emptyLineItem()],
  };
}

export default function DocumentEditor() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const detailQuery = useQuery({
    queryKey: ['document', id],
    queryFn: () => documentsApi.get(id!),
    enabled: isEdit,
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<DocumentFormValues>({
    resolver: zodResolver(documentFormSchema),
    defaultValues: blankDocumentForm('invoice'),
  });

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'line_items',
  });

  useEffect(() => {
    if (detailQuery.data) reset(detailToForm(detailQuery.data));
  }, [detailQuery.data, reset]);

  const doc = detailQuery.data;
  const isRegenerate =
    isEdit && (doc?.status === 'ISSUED' || doc?.status === 'ACCEPTED');
  const type = useWatch({ control, name: 'type' });
  const watchedItems = useWatch({ control, name: 'line_items' });
  const watchedDiscount = useWatch({ control, name: 'discount' });
  const watchedEvent = useWatch({ control, name: 'event' });

  const estimate = useMemo(
    () => previewTotals(watchedItems ?? [], watchedDiscount ?? ''),
    [watchedItems, watchedDiscount]
  );

  const createMutation = useMutation({
    mutationFn: (input: CreateDocumentInput) => documentsApi.create(input),
    onSuccess: created => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.success('Draft created');
      navigate(`/documents/${created.id}`);
    },
    onError: () => toast.error('Could not save the draft'),
  });

  const updateMutation = useMutation({
    mutationFn: (input: UpdateDocumentInput) => documentsApi.update(id!, input),
    onSuccess: updated => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['document', id] });
      toast.success(isRegenerate ? 'New revision generated' : 'Changes saved');
      navigate(`/documents/${updated.id}`);
    },
    onError: e => {
      if (getApiErrorStatus(e) === 409) {
        toast.error('This document can no longer be edited.');
      } else {
        toast.error('Could not save changes');
      }
    },
  });

  const onSubmit = (values: DocumentFormValues) => {
    const line_items = values.line_items.map(li => ({
      description: li.description.trim(),
      amount: li.amount.trim(),
    }));
    const discount = values.discount?.trim() ? values.discount.trim() : '0';
    if (isEdit) {
      const payload: UpdateDocumentInput = {
        contact: values.contact || null,
        event: values.event || null,
        line_items,
        discount,
        due_date: values.due_date || null,
        valid_until: values.valid_until || null,
        terms: values.terms || null,
        accept_line: values.accept_line || null,
        prepared_label: values.prepared_label || null,
      };
      updateMutation.mutate(payload);
    } else {
      const payload: CreateDocumentInput = {
        type: values.type,
        client_id: values.client_id,
        contact: values.contact || null,
        event: values.event || null,
        line_items,
        discount,
        issue_date: values.issue_date || null,
        due_date: values.due_date || null,
        // Omitted (not null) when empty: the server then applies the default
        // validity window (F3.4). An explicit null means "no expiry", which
        // on create is expressed by clearing the date after creation.
        ...(values.valid_until ? { valid_until: values.valid_until } : {}),
        terms: values.terms || null,
        accept_line: values.accept_line || null,
        prepared_label: values.prepared_label || null,
      };
      createMutation.mutate(payload);
    }
  };

  if (isEdit && detailQuery.isLoading) {
    return (
      <div data-testid="editor-loading" className="flex justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isEdit && (detailQuery.isError || !doc)) {
    return (
      <div data-testid="editor-error">
        <EmptyState
          title="Couldn't load the document"
          description="Something went wrong. Please try again."
          action={{ label: 'Retry', onClick: () => detailQuery.refetch() }}
        />
      </div>
    );
  }

  // Guard: an edit route on a non-editable document (design 3.5b).
  if (
    isEdit &&
    doc &&
    (doc.imported || !['DRAFT', 'ISSUED', 'ACCEPTED'].includes(doc.status))
  ) {
    return (
      <div
        data-testid="editor-not-editable"
        className="mx-auto max-w-md rounded-2xl border border-hairline bg-white px-6 py-12 text-center shadow-sm"
      >
        <div className="mx-auto mb-4 flex h-[52px] w-[52px] items-center justify-center rounded-[13px] bg-gray-100">
          <LockClosedIcon className="h-6 w-6 text-gray-500" />
        </div>
        <h3 className="font-serif text-[19px] font-medium text-gray-900">
          This document can't be edited
        </h3>
        <p className="mx-auto mt-1.5 max-w-xs text-[13px] text-gray-500">
          {doc.imported
            ? 'Imported documents are view/download-only.'
            : doc.status === 'PAID'
              ? 'A PAID document cannot be edited or regenerated. Corrections require a void + replacement.'
              : `A ${doc.status} document cannot be edited or regenerated.`}
        </p>
        <button
          type="button"
          onClick={() => navigate(`/documents/${doc.id}`)}
          className="mt-5 inline-flex h-10 items-center rounded-[9px] border-[1.5px] border-[#d7dfe9] bg-white px-4 text-[13.5px] font-semibold text-gray-700 hover:bg-gray-50"
        >
          Back to document
        </button>
      </div>
    );
  }

  const submitting = createMutation.isPending || updateMutation.isPending;
  const submitLabel = submitting
    ? 'Saving…'
    : isEdit
      ? isRegenerate
        ? 'Save & regenerate'
        : 'Save draft'
      : 'Create draft';

  return (
    <div data-testid="document-editor" className="space-y-6">
      {/* Header with actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif text-[24px] font-medium text-gray-900">
          {isEdit
            ? isRegenerate
              ? 'Edit & regenerate'
              : 'Edit draft'
            : 'New document'}
          {isEdit && doc?.doc_no && (
            <span className="ml-2 font-mono text-[18px] text-gray-400">
              {doc.doc_no}
            </span>
          )}
        </h1>
        <div className="flex gap-2.5">
          <button
            type="button"
            onClick={() => navigate(isEdit ? `/documents/${id}` : '/documents')}
            className="inline-flex h-[42px] items-center rounded-[9px] border-[1.5px] border-[#d7dfe9] bg-white px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="editor-form"
            data-testid="editor-submit"
            disabled={submitting}
            className="inline-flex h-[42px] items-center rounded-[9px] bg-primary-600 px-5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {submitLabel}
          </button>
        </div>
      </div>

      {isRegenerate && (
        <div
          data-testid="regenerate-notice"
          role="alert"
          className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3.5"
        >
          <ExclamationTriangleIcon className="mt-px h-[18px] w-[18px] flex-shrink-0 text-amber-600" />
          <div>
            <p className="text-[13px] font-semibold text-amber-900">
              Saving creates a new immutable revision (–R version)
            </p>
            <p className="mt-0.5 text-[12.5px] text-amber-700">
              The legal number {doc?.doc_no} stays unchanged. The prior version
              remains downloadable (F5.3).
            </p>
          </div>
        </div>
      )}

      <form
        id="editor-form"
        onSubmit={handleSubmit(onSubmit)}
        data-testid="editor-form"
        className="grid grid-cols-1 items-start gap-7 lg:grid-cols-[1fr_400px]"
      >
        {/* LEFT: form */}
        <div className="space-y-5">
          {/* Header card */}
          <div className={card}>
            <span className={sectionHead}>Header</span>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {!isEdit && (
                <div>
                  <label className={labelClass}>Type</label>
                  <select
                    data-testid="type-select"
                    aria-label="Type"
                    className={inputClass}
                    {...register('type')}
                  >
                    <option value="invoice">Invoice</option>
                    <option value="quote">Quote</option>
                  </select>
                  <p className="mt-1.5 text-[11.5px] text-gray-400">
                    Type is locked once the draft is created.
                  </p>
                </div>
              )}

              <div className={isEdit ? 'sm:col-span-2' : ''}>
                <label className={labelClass}>Client</label>
                <Controller
                  control={control}
                  name="client_id"
                  render={({ field }) => (
                    <ClientCombobox
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.client_id?.message}
                    />
                  )}
                />
              </div>

              <div>
                <label className={labelClass}>Contact</label>
                <input
                  data-testid="contact-input"
                  aria-label="Contact"
                  className={inputClass}
                  {...register('contact')}
                />
              </div>

              <div>
                <label className={labelClass}>Event name</label>
                <input
                  data-testid="event-input"
                  aria-label="Event name"
                  className={inputClass}
                  {...register('event')}
                />
              </div>

              {type === 'invoice' ? (
                <div>
                  <label className={labelClass}>Payment due date</label>
                  <input
                    type="date"
                    data-testid="due-date-input"
                    aria-label="Payment due date"
                    className={inputClass}
                    {...register('due_date')}
                  />
                  <p className="mt-1.5 text-[11.5px] text-gray-400">
                    Defaults to issue date + 7 days if left blank.
                  </p>
                </div>
              ) : (
                <div>
                  <label className={labelClass}>Valid until (optional)</label>
                  <input
                    type="date"
                    data-testid="valid-until-input"
                    aria-label="Valid until"
                    className={inputClass}
                    {...register('valid_until')}
                  />
                  <p className="mt-1.5 text-[11.5px] text-gray-400">
                    Leave blank to omit a validity date (never flagged expired).
                  </p>
                </div>
              )}

              <div>
                <label className={labelClass}>Prepared-by label</label>
                <input
                  data-testid="prepared-label-input"
                  aria-label="Prepared-by label"
                  className={inputClass}
                  {...register('prepared_label')}
                />
              </div>
            </div>
          </div>

          {/* Line items (F3.2) */}
          <div className={card}>
            <div className="flex items-center justify-between">
              <span className={sectionHead}>Line items</span>
              <button
                type="button"
                data-testid="add-line-item"
                onClick={() => append(emptyLineItem())}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border-[1.5px] border-primary-200 bg-primary-50 px-3.5 text-[13px] font-semibold text-primary-600 hover:bg-primary-100"
              >
                <PlusIcon className="h-4 w-4" /> Add line
              </button>
            </div>

            {typeof errors.line_items?.message === 'string' && (
              <p className="mt-2 text-sm text-red-600">
                {errors.line_items.message}
              </p>
            )}

            <div className="mt-4 space-y-2.5" data-testid="line-items">
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  data-testid="line-item-row"
                  className="flex items-start gap-2.5"
                >
                  <div className="flex-1">
                    <input
                      data-testid={`line-item-desc-${index}`}
                      placeholder="Description"
                      aria-label="Line item description"
                      className={inputClass}
                      {...register(`line_items.${index}.description` as const)}
                    />
                    {errors.line_items?.[index]?.description && (
                      <p className="mt-1 text-xs text-red-600">
                        {errors.line_items[index]?.description?.message}
                      </p>
                    )}
                  </div>
                  <div className="w-[130px]">
                    <input
                      data-testid={`line-item-amount-${index}`}
                      inputMode="decimal"
                      placeholder="0.00"
                      aria-label="Line item amount"
                      className={`${inputClass} text-right font-mono`}
                      {...register(`line_items.${index}.amount` as const)}
                    />
                    {errors.line_items?.[index]?.amount && (
                      <p className="mt-1 text-right text-xs text-red-600">
                        {errors.line_items[index]?.amount?.message}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      aria-label="Move up"
                      data-testid={`line-item-up-${index}`}
                      disabled={index === 0}
                      onClick={() => move(index, index - 1)}
                      className="flex h-11 w-8 items-center justify-center rounded-lg border-[1.5px] border-gray-200 text-xs text-gray-500 disabled:opacity-30"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      aria-label="Move down"
                      data-testid={`line-item-down-${index}`}
                      disabled={index === fields.length - 1}
                      onClick={() => move(index, index + 1)}
                      className="flex h-11 w-8 items-center justify-center rounded-lg border-[1.5px] border-gray-200 text-xs text-gray-500 disabled:opacity-30"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      aria-label="Remove line"
                      data-testid={`line-item-remove-${index}`}
                      disabled={fields.length === 1}
                      onClick={() => remove(index)}
                      className="flex h-11 w-9 items-center justify-center rounded-lg border-[1.5px] border-red-200 text-red-600 disabled:opacity-30"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex items-center justify-end gap-4 border-t border-gray-100 pt-4">
              <label className={`${labelClass} mb-0`}>Discount</label>
              <input
                data-testid="discount-input"
                aria-label="Discount"
                inputMode="decimal"
                placeholder="0.00"
                className={`${inputClass} w-[130px] text-right font-mono`}
                {...register('discount')}
              />
            </div>
          </div>

          {/* Terms & acceptance (F3.5) */}
          <div className={card}>
            <span className={sectionHead}>Terms &amp; acceptance</span>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>Terms</label>
                <textarea
                  data-testid="terms-input"
                  aria-label="Terms"
                  rows={3}
                  className={`${inputClass} h-auto py-2.5 leading-relaxed`}
                  {...register('terms')}
                />
              </div>
              <div>
                <label className={labelClass}>Acceptance line</label>
                <textarea
                  data-testid="accept-line-input"
                  aria-label="Acceptance line"
                  rows={3}
                  className={`${inputClass} h-auto py-2.5 leading-relaxed`}
                  {...register('accept_line')}
                />
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: sticky summary + preview */}
        <div className="space-y-5 lg:sticky lg:top-6">
          <div className={card}>
            <div className="mb-3.5 flex items-center justify-between">
              <span className={sectionHead}>Summary</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-gray-400">
                Live estimate
              </span>
            </div>
            <dl className="flex flex-col gap-2.5" data-testid="totals-estimate">
              <div className="flex justify-between text-sm text-gray-600">
                <dt>Subtotal</dt>
                <dd data-testid="estimate-subtotal" className="font-mono">
                  {formatMoney(estimate.subtotal)}
                </dd>
              </div>
              <div className="flex justify-between text-sm text-gray-600">
                <dt>Discount</dt>
                <dd data-testid="estimate-discount" className="font-mono">
                  −{formatMoney(estimate.discount)}
                </dd>
              </div>
              <div className="my-0.5 h-px bg-hairline" />
              <div className="flex items-baseline justify-between">
                <dt className="text-[15px] font-bold text-gray-900">Total</dt>
                <dd
                  data-testid="estimate-total"
                  className="font-mono text-[22px] font-semibold text-gray-900"
                >
                  {formatMoney(estimate.total)}
                </dd>
              </div>
            </dl>
            <p className="mt-3 text-[11.5px] leading-relaxed text-gray-400">
              Estimate only — the server computes authoritative totals on save.
            </p>
          </div>

          {/* Branded preview mock (client-side) */}
          <div className={`${card} p-4`}>
            <div className="mb-3 flex items-center justify-between">
              <span className={`${sectionHead} text-[13px]`}>Preview</span>
              <span className="font-mono text-[10px] text-gray-400">
                client-side mock
              </span>
            </div>
            <div className="flex flex-col gap-2.5 rounded-[10px] border border-[#eef2f7] bg-[#fdfdfe] p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-serif text-[15px] italic text-gray-900">
                    Lionel J Photography
                  </div>
                  <div className="mt-0.5 font-mono text-[8px] uppercase tracking-[0.15em] text-gray-400">
                    {type === 'invoice' ? 'INVOICE' : 'QUOTE'} · DRAFT
                  </div>
                </div>
                <div className="font-mono text-[9px] text-gray-400">
                  Nassau, BS
                </div>
              </div>
              <div className="h-px bg-[#eef2f7]" />
              {watchedEvent && (
                <div className="text-[10px] text-gray-500">{watchedEvent}</div>
              )}
              {(watchedItems ?? [])
                .filter(li => li.description || li.amount)
                .slice(0, 5)
                .map((li, i) => (
                  <div
                    key={i}
                    className="flex justify-between text-[10.5px] text-gray-700"
                  >
                    <span className="truncate pr-2">
                      {li.description || '—'}
                    </span>
                    <span className="font-mono">
                      {li.amount ? formatMoney(li.amount) : '$0.00'}
                    </span>
                  </div>
                ))}
              <div className="mt-0.5 flex justify-end gap-4 border-t border-[#eef2f7] pt-2">
                <span className="text-[10px] text-gray-500">Total</span>
                <span className="font-mono text-[13px] font-semibold text-gray-900">
                  {formatMoney(estimate.total)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
